import { Component } from '@theme/component';
import { morph } from '@theme/morph';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent } from '@shopify/events';

// One modal across every product card; invalidate stale requests when the selected product changes or the dialog closes.
let tcQuickAddActiveRequest = 0;

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {Map<string, Promise<Element>>} */
  #inflightProductGrids = new Map();
  /** @type {ReturnType<typeof setTimeout> | null} */
  #prefetchTimer = null;
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (productCard) return productCard.productPageUrl;

    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = hotspotProduct?.getHotspotProductLink();

    return productLink?.href || '';
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() ?? null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    const chooseButton = this.querySelector('.quick-add__button--choose');
    chooseButton?.addEventListener('pointerenter', this.#scheduleQuickAddPrefetch);
    chooseButton?.addEventListener('pointerleave', this.#cancelQuickAddPrefetch);
    chooseButton?.addEventListener('focus', this.#scheduleQuickAddPrefetch);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(StandardEvents.productSelect, this.#handleProductSelectUpdate);
    this.#cancelQuickAddPrefetch();
    const chooseButton = this.querySelector('.quick-add__button--choose');
    chooseButton?.removeEventListener('pointerenter', this.#scheduleQuickAddPrefetch);
    chooseButton?.removeEventListener('pointerleave', this.#cancelQuickAddPrefetch);
    chooseButton?.removeEventListener('focus', this.#scheduleQuickAddPrefetch);
  }

  /**
   * Updates quick-add button state when product variant is selected
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelectUpdate = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    if (this.dataset.usesSellingPlans === 'true') return;

    // Only flip choose <-> add when both buttons were rendered.
    // Otherwise the flip would hide the sole rendered button and reveal nothing.
    if (this.dataset.rendersBothButtons !== 'true') return;

    const productOptionsCount = this.dataset.productOptionsCount;
    let quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';

    // A single-option card can resolve to an unavailable variant (e.g. re-selecting a
    // sold-out swatch). Keep "Choose" so shoppers reach the picker, not a dead-end disabled "Add".
    if (quickAddButton === 'add' && this.#isSelectedVariantUnavailable()) {
      quickAddButton = 'choose';
    }

    this.setAttribute('data-quick-add-button', quickAddButton);
  };

  /**
   * Whether the card's currently selected swatch maps to an unavailable variant.
   * Reads `data-option-available` off the variant picker's selected option - the same
   * signal the product card uses. Only reports true on an explicit `false`, so an
   * unknown/absent signal leaves the caller's default ("add") untouched.
   * @returns {boolean}
   */
  #isSelectedVariantUnavailable() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.variantPicker?.selectedOption?.dataset.optionAvailable === 'false';
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Fetch at most one product page per card URL at a time; preserve the
   * original Shopify product DOM so the existing variant/cart logic works.
   * @param {string} url
   * @returns {Promise<Element>}
   */
  async #loadQuickAddProduct(url) {
    const cached = this.#cachedContent.get(url);
    if (cached) return cached;
    let pending = this.#inflightProductGrids.get(url);
    if (!pending) {
      pending = this.fetchProductPage(url).then((page) => {
        const productGrid = page?.querySelector('[data-product-grid-content]');
        if (!productGrid) throw new Error('Quick Add product details could not be loaded');
        const fresh = productGrid.cloneNode(true);
        this.#cachedContent.set(url, fresh);
        return fresh;
      });
      this.#inflightProductGrids.set(url, pending);
      pending.then(
        () => { if (this.#inflightProductGrids.get(url) === pending) this.#inflightProductGrids.delete(url); },
        () => { if (this.#inflightProductGrids.get(url) === pending) this.#inflightProductGrids.delete(url); }
      );
    }
    return pending;
  }

  #cancelQuickAddPrefetch = () => {
    if (this.#prefetchTimer !== null) clearTimeout(this.#prefetchTimer);
    this.#prefetchTimer = null;
  };

  #scheduleQuickAddPrefetch = (event) => {
    if (this.dataset.usesSellingPlans === 'true') return;
    // Avoid needless requests from touch scrolls. Hover must be deliberate.
    if (event.type === 'pointerenter' && !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    this.#cancelQuickAddPrefetch();
    this.#prefetchTimer = setTimeout(() => {
      this.#prefetchTimer = null;
      const url = this.productPageUrl;
      if (!url || this.#cachedContent.has(url)) return;
      void this.#loadQuickAddProduct(url).catch(() => {
        // Hover prefetch failure should never block a normal click or show an error.
      });
    }, event.type === 'focus' ? 0 : 160);
  };

  /** Show a product-shaped placeholder in the SAME modal as the real content. */
  #showQuickAddLoading() {
    const container = document.getElementById('quick-add-modal-content');
    if (!container) return;
    container.classList.add('tc-qa-is-loading');
    container.setAttribute('aria-busy', 'true');
    // Static template: product names enter only through textContent, not HTML.
    container.innerHTML = `
      <div class="tc-qa-skeleton__gallery" aria-hidden="true">
        <div class="tc-qa-skeleton__media tc-qa-shimmer"></div>
        <div class="tc-qa-skeleton__thumbnails">
          <span class="tc-qa-shimmer"></span><span class="tc-qa-shimmer"></span>
          <span class="tc-qa-shimmer"></span><span class="tc-qa-shimmer"></span>
        </div>
      </div>
      <div class="tc-qa-skeleton__details">
        <div class="tc-qa-skeleton__title"></div>
        <div class="tc-qa-skeleton__line tc-qa-skeleton__line--short tc-qa-shimmer"></div>
        <div class="tc-qa-skeleton__description" aria-hidden="true">
          <span class="tc-qa-shimmer"></span><span class="tc-qa-shimmer"></span>
          <span class="tc-qa-shimmer"></span>
        </div>
        <div class="tc-qa-skeleton__swatches" aria-hidden="true">
          <span class="tc-qa-shimmer"></span><span class="tc-qa-shimmer"></span>
          <span class="tc-qa-shimmer"></span>
        </div>
        <p class="tc-qa-loading-status" role="status" aria-live="polite">
          <span class="tc-qa-loader" aria-hidden="true"></span>
          Loading product details...
        </p>
      </div>`;
    const heading = container.querySelector('.tc-qa-skeleton__title');
    if (heading) heading.textContent = this.dataset.productTitle || 'Choose your options';
  }

  /** Always provide an actionable fallback when product data cannot load. */
  #showQuickAddLoadError() {
    const container = document.getElementById('quick-add-modal-content');
    if (!container) return;
    container.setAttribute('aria-busy', 'false');
    container.classList.add('tc-qa-is-loading');
    container.replaceChildren();
    const panel = document.createElement('div');
    panel.className = 'tc-qa-load-error';
    const heading = document.createElement('h3');
    heading.textContent = 'Product details could not be loaded';
    const message = document.createElement('p');
    message.textContent = 'Please try again or open the product page.';
    const actions = document.createElement('div');
    actions.className = 'tc-qa-load-error__actions';
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.textContent = 'Try again';
    retry.addEventListener('click', () => {
      void this.handleClick({ preventDefault() {} });
    });
    const details = document.createElement('a');
    details.href = this.productPageUrl;
    details.textContent = 'View product';
    actions.append(retry, details);
    panel.append(heading, message, actions);
    container.append(panel);
  }

  /**
   * Instant-open: show the modal before any product fetch, so the customer
   * sees feedback on the next paint. Never submit from skeleton content.
   * @param {Event} event
   */
  handleClick = async (event) => {
    event.preventDefault();
    const currentUrl = this.productPageUrl;
    if (this.dataset.usesSellingPlans === 'true') {
      if (currentUrl) window.location.href = currentUrl;
      return;
    }
    if (!currentUrl) return;

    this.#cancelQuickAddPrefetch();
    const request = ++tcQuickAddActiveRequest;
    const cached = this.#cachedContent.get(currentUrl);
    if (!cached) this.#showQuickAddLoading();
    this.#openQuickAddModal();

    try {
      const productGrid = cached || await this.#loadQuickAddProduct(currentUrl);
      if (request !== tcQuickAddActiveRequest) return;
      await this.updateQuickAddModal(productGrid.cloneNode(true));
      if (request !== tcQuickAddActiveRequest) return;
      this.#updateVariantPicker(productGrid);
      const container = document.getElementById('quick-add-modal-content');
      container?.classList.remove('tc-qa-is-loading');
      container?.setAttribute('aria-busy', 'false');
    } catch (error) {
      if (request !== tcQuickAddActiveRequest) return;
      console.warn('[quick-add] Product details could not load:', error);
      this.#showQuickAddLoadError();
    }
  };

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;

    const productDetails = productGrid.querySelector('.product-details');
    const productFormComponent = productGrid.querySelector('product-form-component');
    const variantPicker = productGrid.querySelector('variant-picker');
    const productPrice = productGrid.querySelector('product-price');

    // The product-page metafield-backed short description is already present
    // in the fetched product HTML. Extract its text once and place it next to
    // the variant picker in BOTH layouts. The normal page remains untouched.
    const originalDescription = productDetails?.querySelector(
      '[class*="text-block--trekcamp_short_description"]'
    );
    // Reliable source: product-card Shopify Liquid metafield. Product-page HTML
    // block classes are not guaranteed to be present in a rendered preview.
    const descriptionText = (
      this.dataset.tcShortDescription ||
      productGrid.dataset.tcShortDescription ||
      originalDescription?.textContent ||
      ''
    ).replace(/\s+/g, ' ').trim();
    if (descriptionText && variantPicker) {
      if (originalDescription) originalDescription.classList.add('tc-quick-add-original-description');
      const shortDescription = document.createElement('div');
      shortDescription.className = 'tc-quick-add-short-description';
      const paragraph = document.createElement('p');
      paragraph.textContent = descriptionText;
      shortDescription.appendChild(paragraph);
      if (isMobileBreakpoint()) {
        productGrid.insertBefore(shortDescription, productGrid.firstChild);
      } else {
        variantPicker.parentNode?.insertBefore(shortDescription, variantPicker);
      }
    }

    if (isMobileBreakpoint()) {
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');
      productHeader.appendChild(productTitle);
      if (productPrice) productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);

      if (variantPicker) productGrid.appendChild(variantPicker);
      if (productFormComponent) productGrid.appendChild(productFormComponent);
      productDetails?.remove();
    }

    // Shopify's own product picker, image updates and cart form stay intact.
    const payload = productGrid.getAttribute('view-event-payload') || '';
    modalContent.setAttribute('view-event-payload', payload);
    morph(modalContent, productGrid);

    // Ensure the metafield description survives the modal DOM morph on both
    // desktop and mobile. This does not alter the original product page.
    if (descriptionText && !modalContent.querySelector('.tc-quick-add-short-description')) {
      const selectedPicker = modalContent.querySelector('variant-picker');
      if (selectedPicker) {
        const shortDescription = document.createElement('div');
        shortDescription.className = 'tc-quick-add-short-description';
        const paragraph = document.createElement('p');
        paragraph.textContent = descriptionText;
        shortDescription.appendChild(paragraph);
        selectedPicker.before(shortDescription);
      }
    }

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(StandardEvents.cartLinesUpdate, this.handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
    this.addEventListener(DialogCloseEvent.eventName, this.#invalidatePendingQuickAdd, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog on successful cart update
   * @param {CartLinesUpdateEvent} event - The cart lines update event
   */
  handleCartUpdate = (event) => {
    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;
        this.closeDialog();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  /** @param {ProductSelectEvent} event - The product select event */
  #handleProductSelect = (event) => {
    // Wait for variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const anchorElement = /** @type {HTMLAnchorElement} */ (html.querySelector('.view-product-title a'));
        const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
        const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

        if (!anchorElement) return;

        if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
        if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[quick-add] Event promise rejected:', error);
      });
  };

  #invalidatePendingQuickAdd = () => {
    ++tcQuickAddActiveRequest;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
